import { matchPath } from 'react-router'

import { routeName } from '~/@common/constants/route'
import type { RestrictionPrefix } from '~/@common/types/permission'

/**
 * 백엔드가 정의하는 featurePermission key (FE SSOT)
 */
export const FEATURE_KEYS = [
  // ── 챌린지
  'ARITHMETIC_WORKSHEET', // 챌린지 유형표의 연산 트랙 — 탭·뱃지 등 연산 표기 전반
  'CHALLENGE_SETTING', // 챌린지 페이지의 "선생님이 설정한 챌린지" 탭 + 심플홈 AssignedLearningSection 가드
  'REMASTER_SMILE_CHALLENGE',

  // ── 강의 / 학습 보조
  'ASSIGN_LECTURE', // 선생님이 배정한 강의
  'CAS', // 자동시험
  'CAS_WRITE', // 자동시험 풀기 — 학부모 토큰에만 실린다
  'DAILY_WORKSHEET',
  'LEARNING_FOUR_WEEKS_PROBLEM',
  'LEARNING_TIMER',
  'LECTURE', // 강의 콘텐츠 자체 (콘텐츠 레벨)
  'SELF_LEARNING',

  // ── 라이브 레슨
  'LIVE_LESSON',

  // ── 필기
  'HAND_WRITTEN',

  // ── 학부모 / 자녀
  'ATTENDANCE',
  'CHILDREN_LIST',
  'NOTICE',
  'REPORT',

  // ── 알림 / QR / 피드백 / 가이드
  'NOTIFICATION_CENTER',
  'PUSH',
  'QR',
  'SEND_FEEDBACK',
  'USER_GUIDE',

  // ── 프로필 / 학생 정보
  'STUDENT_INFO_UPDATE',

  // 교재 (WORKBOOK)
  'SIGNATURE_WORKBOOK',
  'ACADEMY_GROUP_WORKBOOK',
  'SIGNATURE_WORKBOOK_LECTURE',
  'SIGNATURE_WORKBOOK_PDF',
  'SIGNATURE_WORKBOOK_WRITE',
  'SIGNATURE_WORKBOOK_PROBLEM_SOLVED_VIDEO',
  'CUSTOM_WORKBOOK_WRITE', // 교재종류에 PUBLIC, SCHOOL, CUSTOM이 있지만 내부의 기능이 다르지 않아 CUSTOM_WORKBOOK~ 세트로 문풀동, 풀이 관리
  'CUSTOM_WORKBOOK_PROBLEM_SOLVED_VIDEO',
  // ── 챌린지 (CHALLENGE)
  'CHALLENGE_WORKSHEET',
  // ── 학습지 (WORKSHEET)
  'CUSTOM_WORKSHEET_WRITE',
  'CUSTOM_WORKSHEET_PROBLEM_SOLVED_VIDEO',

  // ── 시험 (EXAM)
  'EXAM_WORKSHEET',
  'EXAM_WORKSHEET_WRITE',
  'EXAM_WORKSHEET_PROBLEM_SOLVED_VIDEO',
] as const

export type StudentFeatureKey = (typeof FEATURE_KEYS)[number]

/**
 * 기능 권한 — restriction dot-notation prefix → StudentFeatureKey 매핑 (SSOT)
 */
const RESTRICTION_FEATURE_MAP: Record<RestrictionPrefix, StudentFeatureKey> = {
  // ── 챌린지
  'mathflat.student_parent.arithmetic_worksheet': 'ARITHMETIC_WORKSHEET',
  'mathflat.student_parent.challenge_setting': 'CHALLENGE_SETTING',
  'mathflat.student_parent.remaster_smile_challenge': 'REMASTER_SMILE_CHALLENGE',

  // ── 강의 / 학습 보조
  'mathflat.student_parent.assign_lecture': 'ASSIGN_LECTURE',
  'mathflat.student_parent.cas': 'CAS',
  'mathflat.student_parent.cas.write': 'CAS_WRITE',
  'mathflat.student_parent.daily_worksheet': 'DAILY_WORKSHEET',
  'mathflat.student_parent.learning_four_weeks_problem': 'LEARNING_FOUR_WEEKS_PROBLEM',
  'mathflat.student_parent.learning_timer': 'LEARNING_TIMER',
  'mathflat.student_parent.lecture': 'LECTURE',
  'mathflat.student_parent.self_learning': 'SELF_LEARNING',

  // ── 라이브 레슨
  'mathflat.student_parent.live_lesson': 'LIVE_LESSON',

  // ── 필기
  'mathflat.student_parent.hand_written': 'HAND_WRITTEN',

  // ── 학부모 / 자녀
  'mathflat.student_parent.attendance': 'ATTENDANCE',
  'mathflat.student_parent.children_list': 'CHILDREN_LIST',
  'mathflat.student_parent.notice': 'NOTICE',
  'mathflat.student_parent.report': 'REPORT',

  // ── 알림 / QR / 피드백 / 가이드
  'mathflat.student_parent.notification_center': 'NOTIFICATION_CENTER',
  'mathflat.student_parent.push': 'PUSH',
  'mathflat.student_parent.qr': 'QR',
  'mathflat.student_parent.send_feedback': 'SEND_FEEDBACK',
  'mathflat.student_parent.user_guide': 'USER_GUIDE',

  // ── 프로필 / 학생 정보
  'mathflat.student_parent.student_info.update': 'STUDENT_INFO_UPDATE',

  // ── 교재 (WORKBOOK)
  'mathflat.student_parent.signature_workbook': 'SIGNATURE_WORKBOOK',
  'mathflat.student_parent.signature_workbook_lecture': 'SIGNATURE_WORKBOOK_LECTURE',
  'mathflat.student_parent.signature_workbook_pdf': 'SIGNATURE_WORKBOOK_PDF',
  'mathflat.student_parent.signature_workbook.write': 'SIGNATURE_WORKBOOK_WRITE',
  'mathflat.student_parent.signature_workbook.problem_solved_video':
    'SIGNATURE_WORKBOOK_PROBLEM_SOLVED_VIDEO',
  'mathflat.student_parent.academy_group_workbook': 'ACADEMY_GROUP_WORKBOOK',
  // 시중/교과서/커스텀 모두 CUSTOM_WORKBOOK_WRITE 로 통합 (내부의 기능이 다르지 않아 CUSTOM_WORKBOOK~ 세트로 문풀동, 풀이 관리)
  'mathflat.student_parent.custom_workbook.write': 'CUSTOM_WORKBOOK_WRITE',
  // 풀이동영상 — 시중/교과서/커스텀 모두 CUSTOM_WORKBOOK_PROBLEM_SOLVED_VIDEO 로 사용
  'mathflat.student_parent.custom_workbook.problem_solved_video':
    'CUSTOM_WORKBOOK_PROBLEM_SOLVED_VIDEO',
  // ── 챌린지 (CHALLENGE)
  'mathflat.student_parent.challenge_worksheet': 'CHALLENGE_WORKSHEET',

  // ── 학습지 (WORKSHEET)
  'mathflat.student_parent.custom_worksheet.write': 'CUSTOM_WORKSHEET_WRITE',
  'mathflat.student_parent.custom_worksheet.problem_solved_video':
    'CUSTOM_WORKSHEET_PROBLEM_SOLVED_VIDEO',

  // ── 시험 (EXAM)
  'mathflat.student_parent.exam_worksheet': 'EXAM_WORKSHEET',
  'mathflat.student_parent.exam_worksheet.write': 'EXAM_WORKSHEET_WRITE',
  'mathflat.student_parent.exam_worksheet.problem_solved_video':
    'EXAM_WORKSHEET_PROBLEM_SOLVED_VIDEO',
}

/** StudentFeatureKey → restriction prefix 역참조 */
const FEATURE_RESTRICTION_MAP: Record<StudentFeatureKey, string> = Object.fromEntries(
  Object.entries(RESTRICTION_FEATURE_MAP).map(([prefix, key]) => [key, prefix]),
) as Record<StudentFeatureKey, string>

/**
 * 라우트 권한 — route → 라우트 진입에 필요한 feature key 배열.
 *
 * 시맨틱: 등록된 키들 중 **하나라도 허용**이면 통과.
 * 즉, 라우트가 막히려면 등록된 키 **전부가 차단**되어야 함 (AND 조건).
 */
const ROUTE_PERMISSION_MAP: Partial<Record<string, StudentFeatureKey[]>> = {
  // 워크북 종류별 전용 라우트
  [routeName.student.customSignatureStudentWorkbookList]: ['SIGNATURE_WORKBOOK'],
  [routeName.student.academyGroupStudentWorkbookList]: ['ACADEMY_GROUP_WORKBOOK'],
  // 챌린지 페이지 진입 — CHALLENGE_WORKSHEET 차단 시 막힘.
  [routeName.student.challenge]: ['CHALLENGE_WORKSHEET'],
  [routeName.student.cas]: ['CAS_WRITE'],
  [routeName.student.casByAssignedId]: ['CAS_WRITE'],
  [routeName.notification]: ['NOTIFICATION_CENTER'],
  // 강의류
  [routeName.student.lectureDetail]: ['LECTURE'],
  [routeName.student.assignedLectureDetail]: ['ASSIGN_LECTURE'],
  [routeName.student.signatureWorkbookLectureDetail]: ['SIGNATURE_WORKBOOK_LECTURE'],
  [routeName.student.lectureList]: ['ASSIGN_LECTURE'],
  // 보충/심화 학습 (self-learning) — SELF_LEARNING 차단 시 진입 불가
  [routeName.student.studentLearningProcess]: ['SELF_LEARNING'],
  // 사용가이드 동영상 페이지 — USER_GUIDE 차단 시 진입 불가
  [routeName.guideVideo]: ['USER_GUIDE'],
  // 시그니처 교재 전용 강의 리스트 — SIGNATURE_WORKBOOK_LECTURE 차단 시 진입 불가
  [routeName.student.signatureWorkbookLectureList]: ['SIGNATURE_WORKBOOK_LECTURE'],

  // 학부모 전용 라우트 (학생 토큰에 해당 restriction 내려옴)
  [routeName.parent.attendance]: ['ATTENDANCE'],
  [routeName.parent.report]: ['REPORT'],
  [routeName.parent.notice]: ['NOTICE'],
  [routeName.parent.noticeDetail]: ['NOTICE'],
  [routeName.parent.casByAssignedId]: ['CAS'],

  // 교재 목록: 2개 키 모두 차단되어야 막힘 (하나라도 허용이면 통과).
  [routeName.student.studentWorkbook]: ['SIGNATURE_WORKBOOK_WRITE', 'CUSTOM_WORKBOOK_WRITE'],

  // 학습지 목록: 2개 *_WRITE 키 모두 차단되어야 막힘 (하나라도 허용이면 통과).
  [routeName.student.studentWorksheet]: ['EXAM_WORKSHEET_WRITE', 'CUSTOM_WORKSHEET_WRITE'],
}

/**
 * restriction 패턴이 대상 경로에 매칭되는지 확인
 *
 * - "mathflat.student.signature-workbook.*" → prefix + 하위 모두 매칭
 * - "mathflat.student.signature-workbook.write" → 정확히 일치만 매칭
 */
function matchRestriction(pattern: string, target: string): boolean {
  if (pattern.endsWith('.*')) {
    const prefix = pattern.slice(0, -2)
    return target === prefix || target.startsWith(prefix + '.')
  }
  return pattern === target
}

export class FeaturePermission {
  private readonly _restrictions: readonly string[]

  constructor(restrictions: string[]) {
    this._restrictions = restrictions
  }

  /** 대상 경로가 restriction 에 매칭되는지 여부 (내부 헬퍼) */
  private isRestricted(target: string): boolean {
    return this._restrictions.some((pattern) => matchRestriction(pattern, target))
  }

  /** 기능 권한 — StudentFeatureKey 가 허용되는지 여부. 매핑된 prefix 가 restricted 면 false */
  isAllowed(key: StudentFeatureKey): boolean {
    const prefix = FEATURE_RESTRICTION_MAP[key]
    if (!prefix) return true
    return !this.isRestricted(prefix)
  }

  /**
   * 라우트 권한 — 주어진 pathname 이 접근 허용되는지 여부.
   *
   * ROUTE_PERMISSION_MAP 의 라우트와 pathname 이 매칭되는 항목에 대해,
   * 등록된 키 중 **하나라도 허용**이면 통과 (모두 차단되어야 막힘).
   * 매핑되지 않은 라우트는 항상 통과.
   */
  isRouteAllowed(pathname: string): boolean {
    return Object.entries(ROUTE_PERMISSION_MAP).every(([route, keys]) => {
      if (!matchPath(route, pathname)) return true
      return !keys?.length || keys.some((key) => this.isAllowed(key))
    })
  }
}
